import React, { useContext, useMemo, useRef, useState } from "react";
import "./style.scss";
import BackIcon from "../../../assets/Header/backIcon.svg";
import NextIcon from "../../../assets/Header/nextIcon.svg";
import {
  useCompleteOrderCreation,
  useGetBatchSummary,
  useGetCsvData,
} from "../../../hooks/useOrderBatches";
import { useNavigate, useParams } from "react-router-dom";
import { message, Modal, Spin } from "antd";
import orderBatchesApi from "../../../apis/orderBatches";
import Stepper from "../../Stepper";
import { UserWalletContext } from "../../../context/userWalletContext";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import guidelinesApi from "../../../apis/guidelines";
import { SourceProviderContext } from "../../../context/sourceProvider";
import { use } from "react";

const OrderSummary = () => {
  const [batchSummary, setBatchSummary] = useState();
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [fileUpload, setFileUpload] = useState({});
  const [isSuccess, setIsSuccess] = useState(false);
  const [imageUrl, setImageUrl] = useState("");
  const [isImageUploading, setIsImageUploading] = useState(false);
  const {
    getPresignedUrlForCSV,
    attachCsvToBatch,
    getPresignedUrlForRichTextEditor,
  } = orderBatchesApi;
  const { getThumbPathOfImage, getUrlFromKey } = guidelinesApi;
  const [comment, setComment] = useState("");
  const [showModal, setShowModal] = useState(false);
  const token = localStorage.getItem("flowedit_token");
  const { getWalletAccountDetails } = useContext(UserWalletContext);
  const { sourceData } = useContext(SourceProviderContext);
  const [editorContent, setEditorContent] = useState("");
  const params = useParams();
  const navigate = useNavigate();
  const quillRef = useRef(null);

  const imageHandler = () => {
    const input = document.createElement("input");
    input.setAttribute("type", "file");
    input.setAttribute("accept", "image/*");
    input.click();

    input.onchange = async () => {
      const file = input.files[0];
      console.log(file);
      if (file) {
        try {
          setIsImageUploading(true);
          const imageUrl = await uploadImage(file);
          const editor = quillRef.current.getEditor();
          const range = editor.getSelection();
          const imgHtml = `<img src="${imageUrl}" style="width: 100px; height: 100px;" />`;
          editor.clipboard.dangerouslyPasteHTML(range.index, imgHtml);
        } catch (error) {
          console.error("Image upload failed:", error);
        } finally {
          setIsImageUploading(false);
        }
      }
    };
  };

  const modules = useMemo(
    () => ({
      toolbar: {
        container: [
          [{ header: "1" }, { header: "2" }, { font: [] }],
          [{ list: "ordered" }, { list: "bullet" }],
          ["bold", "italic", "underline", "strike", "blockquote"],
          [{ align: [] }],
          [{ color: [] }, { background: [] }],
          ["link", "image"],
          ["clean"],
        ],
        handlers: {
          image: imageHandler,
        },
      },
    }),
    []
  );

  const onSuccessBatchSummary = (res) => {
    if (res.status === 200) {
      setBatchSummary(res.data.data);
    }
  };

  const onErrorBatchSummary = () => {};

  const { data } = useGetBatchSummary(
    "batch_summary",
    params.batchId,
    onSuccessBatchSummary,
    onErrorBatchSummary
  );

  const onSuccessCompleteOrderCreation = (res) => {
    if (res.status === 200) {
      message.success("Order Created Successfully");
      getWalletAccountDetails();
      setShowModal(true);
    } else {
      message.error("Error");
    }
  };

  const onErrorCompleteOrderCreation = (err) => {
    message.error(err?.message);
  };

  const { mutate: completeOrderCreation } = useCompleteOrderCreation(
    onSuccessCompleteOrderCreation,
    onErrorCompleteOrderCreation
  );

  const getKeyPresignedUrl = async (file) => {
    let uploadSuccess = true;
    setIsSuccess(true);
    try {
      if (!file[0].name || !file[0].type) {
        uploadSuccess = false;
        setIsSuccess(false);
        throw new Error(
          "Invalid file detected. Ensure the file has a valid name and type."
        );
      }

      const resp = await getPresignedUrlForCSV({
        batchId: params?.batchId,
        filename: file[0].name,
        mimeType: file[0].type,
      });

      if (resp?.status !== 200 || !resp?.data?.key || !resp?.data?.url) {
        uploadSuccess = false;
        setIsSuccess(false);
        throw new Error("Invalid response from getPresignedUrl.");
      }

      const { key, url } = resp.data;

      const uploadResp = await fetch(url, {
        method: "PUT",
        headers: {
          "Content-Type": file[0].type,
        },
        body: file[0],
      });

      if (!uploadResp.ok) {
        uploadSuccess = false;
        setIsSuccess(false);

        throw new Error(`File upload failed with status: ${uploadResp.status}`);
      }

      if (uploadSuccess) {
        setIsSuccess(true);

        attachCsvToBatch({
          batch: params?.batchId,
          csvKey: key,
        }).then((resp) => {
          if (resp?.data?.data?.isCsvProcessed) {
            message.success("Csv File uploaded successfully");
          } else {
            message.error(resp?.data?.data?.errors[0]);
          }
        });
        setFileUpload(key);
      } else {
        message.error("Please upload a valid file.");
      }
    } catch (err) {
      uploadSuccess = false;
      setIsSuccess(false);
      message.error(
        err?.response?.data?.message ||
          err.message ||
          "An error occurred during upload."
      );
    } finally {
      setIsUploadingImage(false);
    }
    return uploadSuccess;
  };

  const handleAreaClick = () => {
    document?.getElementById("file")?.click();
  };

  const handleUploadCSV = async (file) => {
    if (file) {
      const selectedFile = file[0];

      setFileUpload({});
      setBatchSummary((prev) => ({
        ...prev,
        csvFile: selectedFile,
      }));

      const csvContent = await selectedFile.text();

      const rows = csvContent.split("\n").map((row) => row.split(","));

      const htmlContent = `
        <html>
          <head>
            <style>
              table {
                border-collapse: collapse;
                width: 100%;
                font-family: Arial, sans-serif;
              }
              th, td {
                border: 1px solid #ddd;
                padding: 8px;
                text-align: left;
              }
              th {
                background-color: #f4f4f4;
                font-weight: bold;
              }
            </style>
          </head>
          <body>
            <table>
              ${rows
                .map(
                  (row) =>
                    `<tr>${row
                      .map((cell) => `<td>${cell.trim()}</td>`)
                      .join("")}</tr>`
                )
                .join("")}
            </table>
          </body>
        </html>
      `;

      const blob = new Blob([htmlContent], { type: "text/html" });
      const blobUrl = URL.createObjectURL(blob);

      const iframe = document.getElementById("iframe-preview");

      setImageUrl(blobUrl);
      setIsUploadingImage(true);

      getKeyPresignedUrl(file);
    }
  };

  const isEditorContentEmpty = (content) => {
    if (!content || content.trim() === "") {
      return true;
    }
    // Strip HTML tags and check if there's any meaningful content
    const textContent = content.replace(/<[^>]*>/g, "").trim();
    // Also handle cases like &nbsp; and other HTML entities
    const decodedContent = textContent
      .replace(/&nbsp;/g, " ")
      .replace(/&[a-z]+;/gi, "")
      .trim();
    return decodedContent === "";
  };

  const handleQuote = () => {
    if (isEditorContentEmpty(editorContent)) {
      message.error("Additional Information is required");
      return;
    }
    let payload = {
      comments: [comment],
      attachments: [fileUpload],
      richTextDescription: editorContent,
    };
    completeOrderCreation({ batchId: params?.batchId, payload });
  };

  const handleDownload = async () => {
    const urlDownload = `https://api-horcrux.flixstock.com/api/v1/batch/csv?batch=${params?.batchId}`;
    try {
      const response = await fetch(urlDownload, {
        headers: {
          authorization: `Bearer ${token}`,
        },
      });

      if (response?.status === 200) {
        const buffer = await response.blob();
        const blobUrl = URL.createObjectURL(buffer);
        const link = document.createElement("a");
        link.href = blobUrl;
        link.download = "data.csv";
        link.click();
        URL.revokeObjectURL(blobUrl);

        message.success("CSV file downloaded successfully.");
      } else {
        message.error("Failed to download CSV file.");
      }
    } catch (error) {
      message.error("An error occurred while downloading CSV file.");
    }
  };

  const handleOk = () => {
    setShowModal(false);
    navigate("/order-list");
  };

  const getPaymentConfirmation = () => {
    return (
      <Modal
        title="Payment - Info"
        open={showModal}
        onCancel={handleOk}
        onOk={handleOk}
        className="payment_confirmation_modal_wrapper"
        centered
        width={"45%"}
        height={"40dvh"}
        cancelButtonProps={{ style: { display: "none" } }}
      >
        <div className="confirmation_text">
          We have blocked{" "}
          <span>
            ${batchSummary?.blockedAmount?.primaryQuoteCost?.unitAmount}{" "}
          </span>
          for now. Please note that additional services are not included in this
          cost, and the final amount will be adjusted after segregation.
        </div>
      </Modal>
    );
  };

  const handleEditorChange = (content) => {
    setEditorContent(content);
  };

  const uploadImage = async (file) => {
    const resp = await getPresignedUrlForRichTextEditor({
      batchId: params?.batchId,
      filename: file.name,
      mimeType: file.type,
    });

    if (resp?.status === 200) {
      const { key, url } = resp.data;
      await fetch(url, {
        method: "PUT",
        headers: {
          "Content-Type": file.type,
        },
        body: file,
      });
      const thumbResponse = await getThumbPathOfImage({ key });
      return thumbResponse?.data?.data?.thumbPath?.url;
    }
  };
  const attachFiles = async (file) => {
    const resp = await getPresignedUrlForRichTextEditor({
      batchId: params?.batchId,
      filename: file.name,
      mimeType: file.type,
    });

    if (resp?.status === 200) {
      const { key, url } = resp.data;
      await fetch(url, {
        method: "PUT",
        headers: {
          "Content-Type": file.type,
        },
        body: file,
      });
      const thumbResponse = await getUrlFromKey({ key });
      console.log(thumbResponse);
      return thumbResponse?.data?.data?.url;
    }
  };
  const handleAttachFile = (e) => {
    e.preventDefault();
    const input = document.createElement("input");
    input.setAttribute("type", "file");
    input.setAttribute("accept", "*/*");
    input.click();

    input.onchange = async () => {
      const file = input.files[0];
      if (file) {
        try {
          setIsImageUploading(true);
          const fileUrl = await attachFiles(file);
          message.success("File uploaded successfully");
          const editor = quillRef.current.getEditor();
          let range = editor.getSelection();
          if (!range) {
            range = { index: editor.getLength(), length: 0 }; // Insert at the end if no selection
          }
          const fileHtml = `<a href="${fileUrl}" target="_blank">${file.name}</a>`;
          editor.clipboard.dangerouslyPasteHTML(range.index, fileHtml);
          console.log("fileUrl", fileUrl);
        } catch (error) {
          console.error("File upload failed:", error);
        } finally {
          setIsImageUploading(false);
        }
      }
    };
  };
  return (
    <div className="order_summary_container">
      <div className="order_summary_title_wrapper">
        <h4>
          Order Summary:{" "}
          <span className="sub_title">{batchSummary?.batchName}</span>
        </h4>
      </div>
      <div className="order_summary_form_wrapper">
        <div className="summary_container">
          <div className="order_summary_details_container">
            <form>
              <div className="form_control">
                <label>No. Of Images</label>
                <input
                  placeholder="No. of Image"
                  value={batchSummary?.totalImages}
                  disabled
                />
              </div>
              <div className="form_control2">
                <label>Marketplace</label>
                <div className="content_wrapper">
                  {batchSummary?.marketplaces.length > 0 &&
                    batchSummary?.marketplaces.map((marketplace, id) => {
                      return <div className="content">{marketplace?.name}</div>;
                    })}
                </div>
              </div>
              {batchSummary?.addOnServices.length === 0 ? (
                ""
              ) : (
                <div className="form_control3">
                  <label>Add-on Services</label>
                  <div className="content_wrapper">
                    {batchSummary?.addOnServices.length > 0 &&
                      batchSummary?.addOnServices.map((addOn, index) => {
                        return <div className="content">{addOn}</div>;
                      })}
                  </div>
                </div>
              )}
              <div className="form_control3">
                <div className="wrapper">
                  <label>Any Additional Information <sup>*</sup></label>
                  {!isImageUploading && (
                    <button
                      className="attachFileBtn"
                      onClick={(e) => handleAttachFile(e)}
                    >
                      Attach File
                    </button>
                  )}
                </div>
                <Spin spinning={isImageUploading}>
                  <ReactQuill
                    theme="snow"
                    value={editorContent}
                    onChange={handleEditorChange}
                    modules={modules}
                    ref={quillRef}
                    style={{ height: "200px" }}
                    readOnly={isImageUploading} // Disable editor while image is uploading
                    className={isImageUploading ? "quill-disabled" : ""}
                  />
                </Spin>
              </div>
            </form>
          </div>
          {sourceData?.featureFlag?.attachCsvToOrder && (
            <div className="upload_csv_container">
              <div className="left_wrapper">
                <div className="input_wrapper" onClick={handleAreaClick}>
                  {isSuccess ? (
                    <iframe
                      id="iframe-preview"
                      src={imageUrl}
                      width="100%"
                      height="60"
                      style={{
                        border: "1px solid black",
                        padding: "10px",
                        margin: "20px",
                      }}
                    ></iframe>
                  ) : (
                    <>
                      {" "}
                      <label>Upload CSV</label>
                      <input
                        type="file"
                        accept="*.csv"
                        id="file"
                        onChange={(e) => handleUploadCSV(e.target.files)}
                        onClick={(e) => (e.target.value = null)}
                        className="upload_more_images_btn_input"
                      />
                    </>
                  )}
                </div>
                <div className="text_wrapper">
                  <div className="heading_title">
                    Create/Upload sku wise attribute csv
                  </div>
                  <div className="text_content">
                    Create or upload SKU-wise attribute CSV for efficient
                    product data management. Dummy
                  </div>
                </div>
              </div>
              <div className="right_wrapper">
                <button onClick={() => handleDownload()}>
                  Download CSV format
                </button>
              </div>
            </div>
          )}
        </div>
        <div className="bottom_panel">
          <Stepper activeStep={7} isSeggregation={false} />
          <div className="action_container">
            <button
              className="backBtn"
              onClick={() => navigate(`/sku-list/${params.batchId}`)}
            >
              <img src={BackIcon} alt="backBtn" />
            </button>
            <button className="nextBtn" onClick={handleQuote}>
              Create Order
              {/* <img src={NextIcon} alt="nexticon" /> */}
            </button>
          </div>
        </div>
      </div>
      {showModal && getPaymentConfirmation()}
    </div>
  );
};

export default OrderSummary;
